import { useState, useRef, useCallback, useEffect, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Mic, Send, Camera, Image, File, X, Smile, MapPin, Calendar, BarChart3 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { VoiceRecorder } from './VoiceRecorder';
import { ReplyPreview } from './ReplyPreview';
import { MediaConsentSheet } from './MediaConsentSheet';
import { EmojiKeyboard } from './EmojiKeyboard';
import { LocationPicker } from './LocationPicker';
import { CreateEventSheet } from './CreateEventSheet';
import { CreatePollSheet } from './CreatePollSheet';
import { hapticTap } from '@/lib/native-haptics';
import { Capacitor } from '@capacitor/core';
import { pickPhoto, ensureCameraPermission } from '@/lib/native-camera';
import { usePrivacyConsent } from '@/hooks/usePrivacyConsent';
import type { UnifiedMessage } from './UnifiedMessageBubble';


interface UnifiedMessageInputProps {
  onSendText: (text: string, replyToId?: string) => void;
  onSendMedia: (file: File) => void;
  onSendVoice: (blob: Blob, duration: number) => void;
  onSendLocation?: (lat: number, lng: number, address: string) => void;
  onSendSticker?: (sticker: string) => void;
  onSendEvent?: (eventJson: string) => void;
  onSendPoll?: (pollJson: string) => void;
  onTyping?: () => void;
  disabled?: boolean;
  replyTo?: UnifiedMessage | null;
  onClearReply?: () => void;
  editingContent?: string;
}

export function UnifiedMessageInput({
  onSendText,
  onSendMedia,
  onSendVoice,
  onSendLocation,
  onSendSticker,
  onSendEvent,
  onSendPoll,
  onTyping,
  disabled,
  replyTo,
  onClearReply,
  editingContent,
}: UnifiedMessageInputProps) {
  const [text, setText] = useState(editingContent || '');
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [showMediaConsent, setShowMediaConsent] = useState(false);
  const [showEmojiKeyboard, setShowEmojiKeyboard] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const { t } = useTranslation("common");
  const [showEventSheet, setShowEventSheet] = useState(false);
  const [showPollSheet, setShowPollSheet] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [pendingMediaRef, setPendingMediaRef] = useState<React.RefObject<HTMLInputElement | null> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { hasConsent, grantConsent } = usePrivacyConsent();

  // Sync editing content into input
  useEffect(() => {
    if (editingContent !== undefined) {
      setText(editingContent);
      textareaRef.current?.focus();
    }
  }, [editingContent]);

  const hasText = text.trim().length > 0;

  const handleSendText = () => {
    if (!hasText) return;
    onSendText(text.trim(), replyTo?.id);
    setText('');
    onClearReply?.();
    setShowEmojiKeyboard(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendText();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      Array.from(files).forEach((file) => onSendMedia(file));
    }
    e.target.value = '';
    setShowAttachMenu(false);
  };

  const handleVoiceSend = (blob: Blob, duration: number) => {
    onSendVoice(blob, duration);
    setIsRecordingVoice(false);
  };

  const handleEmojiSelect = useCallback((emoji: string) => {
    setText(prev => prev + emoji);
    textareaRef.current?.focus();
  }, []);

  const handleStickerSend = useCallback((sticker: string) => {
    onSendSticker?.(sticker);
    setShowEmojiKeyboard(false);
  }, [onSendSticker]);

  // Voice recording mode
  if (isRecordingVoice) {
    return (
      <VoiceRecorder
        onSend={handleVoiceSend}
        onCancel={() => setIsRecordingVoice(false)}
      />
    );
  }

  return (
    <div className="border-t border-border/40 bg-background safe-bottom">
      {/* Reply preview */}
      {replyTo && (
        <ReplyPreview
          senderName={replyTo.sender_id === 'self' ? 'You' : 'User'}
          content={replyTo.content}
          onClear={() => onClearReply?.()}
        />
      )}

      {/* Emoji keyboard panel */}
      <EmojiKeyboard
        isOpen={showEmojiKeyboard}
        onClose={() => setShowEmojiKeyboard(false)}
        onSelectEmoji={handleEmojiSelect}
        onSendSticker={handleStickerSend}
      />

      <div className="px-3 py-2.5 flex items-end gap-1.5">
        {/* Plus / attach button */}
        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full shrink-0"
            disabled={disabled}
            onClick={() => {
              hapticTap();
              setShowAttachMenu(!showAttachMenu);
              setShowEmojiKeyboard(false);
            }}
            aria-label={showAttachMenu ? "Close attachment menu" : "Attach file"}
          >
            <AnimatePresence mode="wait">
              {showAttachMenu ? (
                <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
                  <X className="h-5 w-5 text-muted-foreground" />
                </motion.div>
              ) : (
                <motion.div key="plus" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
                  <Plus className="h-5 w-5 text-muted-foreground" />
                </motion.div>
              )}
            </AnimatePresence>
          </Button>

          {/* Attach menu */}
          <AnimatePresence>
            {showAttachMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 8 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                className="absolute bottom-full mb-2 left-0 bg-card border border-border/50 rounded-2xl shadow-elevated p-1.5 flex flex-col gap-0.5 min-w-[160px] z-10"
              >
                {[
                  { icon: Camera, label: 'Camera', color: 'text-primary', action: 'file' as const, ref: cameraInputRef },
                  { icon: Image, label: 'Gallery', color: 'text-secondary', action: 'file' as const, ref: imageInputRef },
                  { icon: File, label: 'File', color: 'text-muted-foreground', action: 'file' as const, ref: fileInputRef },
                  { icon: MapPin, label: 'Location', color: 'text-green-500', action: 'location' as const, ref: null },
                  { icon: Calendar, label: 'Event', color: 'text-orange-500', action: 'event' as const, ref: null },
                  { icon: BarChart3, label: 'Poll', color: 'text-violet-500', action: 'poll' as const, ref: null },
                ].map(({ icon: Icon, label, color, action, ref }) => (
                  <button
                    key={label}
                    onClick={() => {
                      if (action === 'location') {
                        setShowLocationPicker(true);
                        setShowAttachMenu(false);
                        return;
                      }
                      if (action === 'event') {
                        setShowEventSheet(true);
                        setShowAttachMenu(false);
                        return;
                      }
                      if (action === 'poll') {
                        setShowPollSheet(true);
                        setShowAttachMenu(false);
                        return;
                      }
                      if (ref && hasConsent('media_sharing')) {
                        ref.current?.click();
                      } else if (ref) {
                        setPendingMediaRef(ref as React.RefObject<HTMLInputElement | null>);
                        setShowMediaConsent(true);
                        setShowAttachMenu(false);
                      }
                    }}
                    className="flex items-center gap-3 px-3.5 py-2.5 text-[13px] font-medium rounded-xl hover:bg-muted/60 active:bg-muted transition-colors"
                  >
                    <Icon className={`h-4 w-4 ${color}`} />
                    <span>{label}</span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Text input with inline emoji toggle */}
        <div className="flex-1 relative flex items-end">
          <Textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => { setText(e.target.value); onTyping?.(); }}
            onKeyDown={handleKeyDown}
            onFocus={() => { setShowEmojiKeyboard(false); setIsInputFocused(true); }}
            onBlur={() => { setTimeout(() => setIsInputFocused(false), 150); }}
            placeholder={t("typeAMessage")}
            disabled={disabled}
            spellCheck
            autoCorrect="on"
            autoCapitalize="sentences"
            className={cn(
              "flex-1 min-h-[36px] max-h-28 resize-none py-2 px-3.5 rounded-2xl bg-muted/50 border-0 text-base placeholder:text-muted-foreground/50 focus-visible:ring-1 focus-visible:ring-primary/20",
              (isInputFocused || showEmojiKeyboard) ? "pr-10" : ""
            )}
            rows={1}
            aria-label={t("typeAMessage")}
          />
          {/* Emoji button - only visible when input is focused or emoji keyboard is open */}
          <AnimatePresence>
            {(isInputFocused || showEmojiKeyboard) && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="absolute right-1.5 bottom-0.5"
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full shrink-0"
                  disabled={disabled}
                  onClick={() => {
                    hapticTap();
                    setShowEmojiKeyboard(!showEmojiKeyboard);
                    setShowAttachMenu(false);
                  }}
                  aria-label="Open emoji keyboard"
                >
                  <Smile className={`h-4.5 w-4.5 ${showEmojiKeyboard ? 'text-primary' : 'text-muted-foreground/60'}`} />
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Send, or Camera + Mic */}
        <AnimatePresence mode="wait">
          {hasText ? (
            <motion.div
              key="send"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            >
              <Button
                size="icon"
                className="h-9 w-9 rounded-full shrink-0"
                onClick={handleSendText}
                disabled={disabled}
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key="camera-mic"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
              className="flex items-center gap-0.5"
            >
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full shrink-0"
                disabled={disabled}
                onClick={async () => {
                  hapticTap();
                  const launch = async () => {
                    if (Capacitor.isNativePlatform()) {
                      const ok = await ensureCameraPermission();
                      if (!ok) return;
                      const file = await pickPhoto({ source: 'camera' });
                      if (file) onSendMedia(file);
                      setShowAttachMenu(false);
                      return;
                    }
                    cameraInputRef.current?.click();
                  };
                  if (hasConsent('media_sharing')) {
                    launch();
                  } else {
                    setPendingMediaRef(cameraInputRef as React.RefObject<HTMLInputElement | null>);
                    setShowMediaConsent(true);
                  }
                }}
                aria-label="Open camera"
              >
                <Camera className="h-5 w-5 text-muted-foreground" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full shrink-0"
                disabled={disabled}
                onClick={() => {
                  hapticTap();
                  setIsRecordingVoice(true);
                }}
                aria-label="Record voice message"
              >
                <Mic className="h-5 w-5 text-muted-foreground" />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Hidden file inputs */}
      <input ref={cameraInputRef} type="file" accept="image/*,video/*" capture="environment" className="hidden" onChange={handleFileSelect} />
      <input ref={imageInputRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleFileSelect} />
      <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" multiple className="hidden" onChange={handleFileSelect} />

      {/* Media consent sheet */}
      <MediaConsentSheet
        isOpen={showMediaConsent}
        onAllow={async () => {
          const granted = await grantConsent('media_sharing');
          if (granted) {
            setShowMediaConsent(false);
            setTimeout(() => pendingMediaRef?.current?.click(), 100);
            setPendingMediaRef(null);
          }
        }}
        onDismiss={() => {
          setShowMediaConsent(false);
          setPendingMediaRef(null);
        }}
      />

      {/* Location picker */}
      <LocationPicker
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onSendLocation={(lat, lng, address) => onSendLocation?.(lat, lng, address)}
      />

      {/* Event creator */}
      <CreateEventSheet
        isOpen={showEventSheet}
        onClose={() => setShowEventSheet(false)}
        onSend={(json) => onSendEvent?.(json)}
      />

      {/* Poll creator */}
      <CreatePollSheet
        isOpen={showPollSheet}
        onClose={() => setShowPollSheet(false)}
        onSend={(json) => onSendPoll?.(json)}
      />
    </div>
  );
}
